import { useMemo } from 'react'
import styled from 'styled-components'
import { Trans, useTranslation } from 'react-i18next'
import { HealthPlanRelevanceSplit, HealthProgramStatisticsResponse } from '@estercare/ester-shared'
import { theme } from '../../../../theme/theme'
import { BodyMedium, BodySmall, BodySmallMedium, BodyTinyMedium, Bold } from '../../../styles/Typography'
import { ChartCard } from '../../../charts/ChartCard'
import { StatisticsSection } from '../StatisticsSection'
import { ChartLegend, stepLegend } from '../../../charts/Legend'
import { SegmentStack } from '../../../charts/SegmentStack'
import { SeverityByConditionChart } from '../../../charts/SeverityByConditionChart'
import { ChartContainer } from '../../../charts/shared'
import { getOutcomeSteps, getOutcomeValues, getSymptomChangeRows } from '../../../../utils/statistics/followUpOutcomes'
import { NotEnoughFollowUpData } from '../../../charts/NotEnoughData'
import { DonutChart } from '../../../charts/DonutChart'
import { getRelevanceSteps, getRelevanceValues } from '../../../../utils/statistics/healthPlanRelevance'

type Props = {
  statistics?: HealthProgramStatisticsResponse
}

export const HealthProgramSection = ({ statistics }: Props) => {
  const { t } = useTranslation()

  const outcomeSteps = useMemo(() => getOutcomeSteps(t), [t])
  const symptomChangeRows = useMemo(
    () => getSymptomChangeRows(t, statistics?.symptomChanges ?? []),
    [t, statistics?.symptomChanges]
  )

  const participants = statistics?.participants
  const followedSelfCareAdvicePct = statistics?.followedSelfCareAdvicePct
  const soughtCareWhenNeededPct = statistics?.soughtCareWhenNeededPct

  const hasParticipants = typeof participants === 'number'
  const hasOutcomeBars = typeof followedSelfCareAdvicePct === 'number' || typeof soughtCareWhenNeededPct === 'number'
  const hasOverviewCard = hasParticipants || hasOutcomeBars

  return (
    <StatisticsSection
      title={t('admin.statistics.healthProgramTitle')}
      intro={t('admin.statistics.healthProgramIntro')}
    >
      <OverviewRow $singleColumn={!hasOverviewCard}>
        {hasOverviewCard && (
          <OverviewCard>
            {hasParticipants && (
              <FigureRow>
                <FigureValue>{participants}</FigureValue>
                <FigureCaption>{t('admin.statistics.participantsDenominator')}</FigureCaption>
              </FigureRow>
            )}

            {hasParticipants && hasOutcomeBars && <Divider />}

            {hasOutcomeBars && (
              <>
                <OutcomesTitle>{t('admin.statistics.followUpOutcomesTitle')}</OutcomesTitle>
                {typeof followedSelfCareAdvicePct === 'number' && typeof soughtCareWhenNeededPct === 'number' && (
                  <OutcomesDescription>
                    {t('admin.statistics.followUpOutcomesIntro', {
                      selfCarePct: Math.round(followedSelfCareAdvicePct),
                      soughtCarePct: Math.round(soughtCareWhenNeededPct),
                    })}
                  </OutcomesDescription>
                )}
                <BarList>
                  {typeof followedSelfCareAdvicePct === 'number' && (
                    <OutcomeBar label={t('admin.statistics.followedSelfCareTitle')} pct={followedSelfCareAdvicePct} />
                  )}
                  {typeof soughtCareWhenNeededPct === 'number' && (
                    <OutcomeBar label={t('admin.statistics.soughtCareTitle')} pct={soughtCareWhenNeededPct} />
                  )}
                </BarList>
              </>
            )}
          </OverviewCard>
        )}
        <HealthPlanRelevanceCard relevance={statistics?.healthPlanRelevance ?? null} />
      </OverviewRow>

      <>
        <OutcomesEyebrow>{t('admin.statistics.outcomesEyebrow')}</OutcomesEyebrow>
        <OutcomesIntro>{t('admin.statistics.outcomesIntro')}</OutcomesIntro>
        <OutcomesRow>
          <ChartCard
            title={t('admin.statistics.symptomChangeTitle')}
            caption={t('admin.statistics.symptomChangeCaption')}
            legend={
              symptomChangeRows.length > 0 && <ChartLegend align="start" noLabelFlex data={stepLegend(outcomeSteps)} />
            }
          >
            {symptomChangeRows.length > 0 ? (
              <SeverityByConditionChart
                steps={outcomeSteps}
                rows={symptomChangeRows}
                unit={t('admin.statistics.outcomeUnit')}
                showUnitInline
              />
            ) : (
              <NotEnoughFollowUpData />
            )}
          </ChartCard>

          <ChartCard
            title={t('admin.statistics.productivityChangeTitle')}
            caption={
              statistics?.productivityChange ? (
                <Trans
                  i18nKey="admin.statistics.productivityChangeCaptionWithData"
                  values={{
                    improvedPct: Math.round(statistics.productivityChange.improvedPct),
                    unchangedPct: Math.round(statistics.productivityChange.unchangedPct),
                  }}
                  components={{ bold: <Bold /> }}
                />
              ) : (
                t('admin.statistics.productivityChangeCaption')
              )
            }
            legend={
              statistics?.productivityChange && (
                <ChartLegend align="start" noLabelFlex data={stepLegend(outcomeSteps)} />
              )
            }
          >
            {statistics?.productivityChange ? (
              <SegmentStack
                steps={outcomeSteps}
                values={getOutcomeValues(statistics.productivityChange)}
                unit={t('admin.statistics.outcomeUnit')}
                showUnitInline
              />
            ) : (
              <NotEnoughFollowUpData />
            )}
          </ChartCard>
        </OutcomesRow>
      </>
    </StatisticsSection>
  )
}

const HealthPlanRelevanceCard = ({ relevance }: { relevance: HealthPlanRelevanceSplit | null }) => {
  const { t } = useTranslation()
  const steps = useMemo(() => getRelevanceSteps(t), [t])
  const formatPct = (pct: number) => t('admin.statistics.summaryPct', { pct })

  if (!relevance) {
    return (
      <ChartCard title={t('admin.statistics.healthPlanRelevanceTitle')}>
        <NotEnoughFollowUpData />
      </ChartCard>
    )
  }

  const values = getRelevanceValues(relevance)

  return (
    <ChartCard
      title={t('admin.statistics.healthPlanRelevanceTitle')}
      footer={<ChartLegend align="start" noLabelFlex textCase="sentence" data={stepLegend(steps)} />}
    >
      <DonutChart
        steps={steps}
        values={values}
        centerLabel={formatPct(Math.round(relevance.relevantPct))}
        formatValue={formatPct}
      />
    </ChartCard>
  )
}

type OutcomeBarProps = {
  label: string
  pct: number
}

const OutcomeBar = ({ label, pct }: OutcomeBarProps) => (
  <BarRow>
    <BarLabel>{label}</BarLabel>
    <BarTrack>
      <BarFill $pct={pct} />
    </BarTrack>
    <BarPercent>{pct}%</BarPercent>
  </BarRow>
)

const OverviewRow = styled.div<{ $singleColumn: boolean }>`
  display: grid;
  grid-template-columns: ${({ $singleColumn }) => ($singleColumn ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(0, 2fr)')};
  gap: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.large}px;

  @media print {
    grid-template-columns: minmax(0, 1fr);
  }

  @media screen and (max-width: ${theme.breakpoint.xlarge}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${theme.spacing.small}px;
  }
`

const OverviewCard = ChartContainer

const OutcomesEyebrow = styled(BodyTinyMedium)`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${theme.color.blueDark};
  margin-bottom: ${theme.spacing.small}px;

  @media print {
    break-after: avoid;
  }
`

const OutcomesIntro = styled(BodyMedium)`
  color: ${theme.color.blueDark};
  max-width: 760px;
  margin-bottom: ${theme.spacing.mediumLarge}px;

  @media print {
    break-after: avoid;
  }
`

const OutcomesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.large}px;

  @media print {
    grid-template-columns: minmax(0, 1fr);
    margin-bottom: 0;
  }

  @media screen and (max-width: ${theme.breakpoint.xlarge}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${theme.spacing.small}px;
  }
`

const FigureRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.small}px;
  margin-top: ${theme.spacing.small}px;
`

const FigureValue = styled.span`
  font-family: ${theme.font.heading};
  font-size: 56px;
  font-weight: 500;
  line-height: 1;
  color: ${theme.color.plum};
`

const FigureCaption = styled(BodyMedium)`
  color: ${theme.color.grey5};
`

const Divider = styled.div`
  height: 1px;
  background-color: ${theme.color.beigeDark};
  margin: ${theme.spacing.medium}px 0;
`

const OutcomesTitle = styled(BodySmallMedium)`
  margin-bottom: ${theme.spacing.medium}px;
  color: ${theme.color.plum};
`

const OutcomesDescription = styled(BodySmall)`
  color: ${theme.color.blueDark};
  margin-bottom: ${theme.spacing.medium}px;
`

const BarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
`

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr 56px;
  align-items: center;
  gap: ${theme.spacing.medium}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.tiny}px;
  }
`

const BarLabel = styled(BodySmall)`
  color: ${theme.color.grey5};
`

const BarTrack = styled.div`
  height: 20px;
  border-radius: 999px;
  background-color: ${theme.color.beigeDark};
  overflow: hidden;
`

const BarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 999px;
  background-color: ${theme.color.plum};
`

const BarPercent = styled(BodyMedium)`
  color: ${theme.color.grey5};
  text-align: right;
  font-variant-numeric: tabular-nums;
`
